import { useBasicRoadmaps } from '@/domain/roadmap/hooks';
import { initialAppliedFilters } from '@/features/roadmap/store/filter';
import { type AppliedFilters } from '@/features/roadmap/store/filter.types';
import { useRoadmapEditStore } from '@/features/roadmap/store/roadmap';
import { hasAnyFilter, mapFiltersToParams } from '@/features/roadmap/utils/filters';
import { initialValue } from '@/features/roadmap/utils/initialValue';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { RoadmapTab } from '../components/MainPage/UpdateItem/UpdateRoadmapTabs';
import { useRoadmapItemEditor } from './useRoadmapItemEditor';

export type PendingNavigation = { kind: 'select'; id: number } | { kind: 'close' } | { kind: 'clone'; id: number };

export interface UseRoadmapUpdateViewParams {
  onClose: () => void;
  onCloneAction: (id: number) => void;
  closeAfterClone?: boolean;
  initialFilters?: Partial<AppliedFilters>;
  initialRoadmapId?: number | null;
  initialTab?: RoadmapTab;
}

export function useRoadmapUpdateView({
  onClose,
  onCloneAction,
  closeAfterClone = false,
  initialFilters,
  initialRoadmapId,
  initialTab = RoadmapTab.Planning,
}: UseRoadmapUpdateViewParams) {
  const [selectedRoadmapId, setSelectedRoadmapId] = useState<number | null>(() => initialRoadmapId ?? null);
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
  const [activeTab, setActiveTab] = useState<RoadmapTab>(initialTab);
  const [selectedPhaseIds, setSelectedPhaseIds] = useState<Set<number>>(new Set());
  const [indeterminatePhaseIds, setIndeterminatePhaseIds] = useState<Set<number>>(new Set());

  const [draftFilters, setDraftFilters] = useState<AppliedFilters>(() =>
    initialFilters ? { ...initialAppliedFilters, ...initialFilters } : initialAppliedFilters,
  );
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters | null>(() => {
    if (!initialFilters) return null;
    const merged = { ...initialAppliedFilters, ...initialFilters };
    return hasAnyFilter(merged) ? merged : null;
  });

  const queryClient = useQueryClient();

  const {
    roadmap,
    isLoading: isLoadingRoadmap,
    isError: isRoadmapError,
    viewOnly,
    showSaveCancel,
    hasUnsavedChanges,
    cancelForm,
    handleSave,
    isPending,
    overviewRef,
  } = useRoadmapItemEditor(selectedRoadmapId);

  const platformFromRoadmap = !appliedFilters && initialRoadmapId ? roadmap?.product?.platform : undefined;

  const effectiveAppliedFilters = useMemo((): AppliedFilters | null => {
    if (appliedFilters !== null) return appliedFilters;
    if (!platformFromRoadmap) return null;
    return { ...initialAppliedFilters, platform: [platformFromRoadmap] };
  }, [appliedFilters, platformFromRoadmap]);

  const effectiveDraftFilters = useMemo((): AppliedFilters => {
    if (hasAnyFilter(draftFilters)) return draftFilters;
    if (!platformFromRoadmap) return draftFilters;
    return { ...initialAppliedFilters, platform: [platformFromRoadmap] };
  }, [draftFilters, platformFromRoadmap]);

  const queryParams = useMemo(
    () => (effectiveAppliedFilters ? mapFiltersToParams(effectiveAppliedFilters) : null),
    [effectiveAppliedFilters],
  );

  const isFiltered = queryParams !== null;
  const { data: roadmaps, isLoading: isLoadingRoadmaps } = useBasicRoadmaps(queryParams);

  const showActions = !!selectedRoadmapId && showSaveCancel;

  const handleDialogClose = useCallback(() => {
    useRoadmapEditStore.setState({ editableRoadMap: initialValue, isStoreDirty: false });
    onClose();
  }, [onClose]);

  const handleSelectRoadmap = useCallback(
    (id: number) => {
      if (selectedRoadmapId !== null && hasUnsavedChanges()) {
        setPendingNavigation({ kind: 'select', id });
        return;
      }
      queryClient.removeQueries({ queryKey: ['roadmap', 'editable', id] });
      setSelectedRoadmapId(id);
      setSelectedPhaseIds(new Set());
    },
    [hasUnsavedChanges, queryClient, selectedRoadmapId],
  );

  const handleRequestClose = useCallback(() => {
    if (hasUnsavedChanges()) {
      setPendingNavigation({ kind: 'close' });
    } else {
      handleDialogClose();
    }
  }, [hasUnsavedChanges, handleDialogClose]);

  const handleConfirmNavigation = useCallback(() => {
    cancelForm();
    if (pendingNavigation === null) return;
    setPendingNavigation(null);
    if (pendingNavigation.kind === 'close') {
      handleDialogClose();
    } else if (pendingNavigation.kind === 'clone') {
      onCloneAction(pendingNavigation.id);
      if (closeAfterClone) handleDialogClose();
    } else {
      queryClient.removeQueries({ queryKey: ['roadmap', 'editable', pendingNavigation.id] });
      setSelectedRoadmapId(pendingNavigation.id);
      setSelectedPhaseIds(new Set());
    }
  }, [cancelForm, closeAfterClone, handleDialogClose, onCloneAction, pendingNavigation, queryClient]);

  const handleCancelNavigation = useCallback(() => {
    setPendingNavigation(null);
  }, []);

  const handleCloneRoadmap = useCallback(
    (id: number) => {
      if (hasUnsavedChanges()) {
        setPendingNavigation({ kind: 'clone', id });
        return;
      }
      onCloneAction(id);
      if (closeAfterClone) handleDialogClose();
    },
    [closeAfterClone, handleDialogClose, hasUnsavedChanges, onCloneAction],
  );

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges()) {
      setPendingNavigation({ kind: 'close' });
    } else {
      cancelForm();
      handleDialogClose();
    }
  }, [cancelForm, handleDialogClose, hasUnsavedChanges]);

  const handleApplyFilters = useCallback((filters: AppliedFilters) => {
    setAppliedFilters(filters);
    setSelectedRoadmapId(null);
  }, []);

  const handleClearFilters = useCallback(() => {
    setDraftFilters(initialAppliedFilters);
    setAppliedFilters(null);
    setSelectedRoadmapId(null);
    useRoadmapEditStore.setState({ editableRoadMap: initialValue, isStoreDirty: false });
  }, []);

  return {
    selectedRoadmapId,
    pendingNavigation,
    activeTab,
    setActiveTab,
    selectedPhaseIds,
    setSelectedPhaseIds,
    indeterminatePhaseIds,
    setIndeterminatePhaseIds,
    effectiveAppliedFilters,
    effectiveDraftFilters,
    setDraftFilters,
    roadmaps,
    isLoadingRoadmaps,
    isFiltered,
    roadmap,
    isLoadingRoadmap,
    isRoadmapError,
    viewOnly,
    showSaveCancel,
    hasUnsavedChanges,
    cancelForm,
    handleSave,
    isPending,
    overviewRef,
    showActions,
    handleDialogClose,
    handleSelectRoadmap,
    handleRequestClose,
    handleConfirmNavigation,
    handleCancelNavigation,
    handleCloneRoadmap,
    handleCancel,
    handleApplyFilters,
    handleClearFilters,
  };
}
